import React, { useState, useEffect } from 'react';
import { Button, FormGroup, Label, Input } from 'reactstrap';
import Select from 'react-select';
import queryString from 'query-string';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { programRequestsApi } from '../api/programRequestsApi';
import { InstitutionField } from '../shared/InstitutionField';
import { RadioCheckGroup } from 'components/academic-affair-report/components/RadioCheckGroup';
import {
    AdditionalCommentsField,
    commentsLengthError,
} from '../shared/AdditionalCommentsField';
import {
    TeachoutField,
    teachoutRequiredError,
    AllStudentsTaughtOutField,
    allStudentsTaughtOutError,
} from '../shared/TeachoutField';
import { createConsolidateProgramsModel } from './consolidateProgramsModel';
import { createConsolidateProgramsFlow } from './createConsolidateProgramsFlow';

const DELIVERY_MODE_OPTIONS = [
    { value: 1, label: 'On Campus Only' },
    { value: 2, label: 'Online Only' },
    { value: 3, label: 'On Campus & Online' },
];

const ON_CAMPUS_ONLINE_NATURE_OPTIONS = [
    { value: 1, label: '100% Online' },
    { value: 2, label: 'Blended' },
];

const normalizeNumber = value =>
    value === '' || value === null || value === undefined ? null : Number(value);

const creditHoursError = form => {
    const minimum = normalizeNumber(form.creditMin);
    const maximum = normalizeNumber(form.creditMax);
    if (minimum === null || maximum === null) {
        return 'Enter both Minimum and Maximum credit hours.';
    }
    if (minimum <= 0 || maximum <= 0) {
        return 'Credit Hours Minimum and Maximum must be greater than 0.';
    }
    if (minimum > maximum) {
        return 'Credit Hours Maximum must be greater than or equal to Minimum.';
    }
    return null;
};

function hasValidationErrors(form) {
    if (
        !form.institutionId ||
        form.sourceProgramIds.length < 2 ||
        !form.formalName?.trim() ||
        !form.cipCode ||
        !form.locations?.length ||
        !form.degreeLevel ||
        form.deliveryMode === null ||
        (form.deliveryMode === 3 && form.onlineNature === null) ||
        creditHoursError(form) !== null ||
        form.sourceProgramIds.some(programId => {
            const teachout = form.sourceProgramTeachouts?.[programId];
            return Boolean(teachoutRequiredError(teachout?.teachoutFlag)) ||
                (teachout?.teachoutFlag === true &&
                    Boolean(allStudentsTaughtOutError(teachout?.allStudentsTaughtOut)));
        })
    ) {
        return true;
    }
    return false;
}

export const ConsolidateProgramsForm = ({
    onBack,
    onSuccess,
    institutionOptions: parentInstitutionOptions = [],
    programsByInstitution = {},
    maximumAdditionalCommentsLength,
    initialModel,
    submitFlow = createConsolidateProgramsFlow,
    mode = 'create',
    requestDetails,
}) => {
    const [form, setForm] = useState(
        () => initialModel ?? createConsolidateProgramsModel()
    );
    const [institutionOptions, setInstitutionOptions] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [cipOptions, setCipOptions] = useState([]);
    const [degreeLevelOptions, setDegreeLevelOptions] = useState([]);
    const [locationOptions, setLocationOptions] = useState([]);
    const [loadingPrograms, setLoadingPrograms] = useState(false);
    const [loadingCips, setLoadingCips] = useState(true);
    const [initializingEdit, setInitializingEdit] = useState(mode === 'edit');
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    useEffect(() => {
        if (parentInstitutionOptions.length > 0) {
            setInstitutionOptions(parentInstitutionOptions);
        } else {
            programRequestsApi.getInstitutions().then(res => {
                const mapped = (res ?? []).map(x => ({ value: x.label, label: x.value }));
                setInstitutionOptions(mapped);
            });
        }

        setLoadingCips(true);
        programRequestsApi.getCipCodes().then(res => {
            const mapped = (res ?? []).map(x => ({
                value: x.cipCode,
                label: `${x.cipCode} - ${x.cipName}`,
            }));
            setCipOptions(mapped);
            setLoadingCips(false);
        });

        programRequestsApi.getDegreeLevels().then(res => {
            const mapped = (res ?? []).map(x => ({ value: x.label, label: x.value }));
            setDegreeLevelOptions(mapped);
        });
    }, []);

    useEffect(() => {
        if (institutionOptions.length === 1 && form.institutionId === null) {
            setForm(f => ({ ...f, institutionId: institutionOptions[0].value }));
        }
    }, [institutionOptions]);

    useEffect(() => {
        if (!form.institutionId) {
            setPrograms([]);
            setLocationOptions([]);
            setForm(f => ({ ...f, sourceProgramIds: [], locations: [] }));
            setInitializingEdit(false);
            return;
        }
        if (mode !== 'edit') {
            setForm(f => ({ ...f, sourceProgramIds: [], locations: [] }));
        }

        const query = queryString.stringify(
            { institutions: form.institutionId },
            { arrayFormat: 'separator', skipEmptyString: true },
        );

        const cached = programsByInstitution[form.institutionId];
        if (Array.isArray(cached) && cached.length > 0) {
            setPrograms(cached);
            setInitializingEdit(false);
        } else {
            setLoadingPrograms(true);
            programRequestsApi.getProgramsForInstitution(query)
                .then(res => setPrograms(res?.programs?.flat() ?? []))
                .finally(() => {
                    setLoadingPrograms(false);
                    setInitializingEdit(false);
                });
        }

        programRequestsApi.getLocations(query).then(res => {
            const mapped = (res?.locations ?? []).map(x => ({ value: x.key, label: x.value }));
            setLocationOptions(mapped);
            if (mode !== 'edit' && mapped.length === 1) {
                setForm(f => ({ ...f, locations: mapped }));
            }
        });
    }, [form.institutionId, mode]);

    const setField = (key, value) => setForm(f => ({ ...f, [key]: value }));

    const setProgramTeachout = (programId, changes) => {
        setForm(current => ({
            ...current,
            sourceProgramTeachouts: {
                ...current.sourceProgramTeachouts,
                [programId]: {
                    ...current.sourceProgramTeachouts?.[programId],
                    ...changes,
                },
            },
        }));
    };

    const handleRadioChange = key => obj => {
        const raw = obj[key];
        setField(key, raw === '' ? null : raw);
    };

    const handleDeliveryModeChange = obj => {
        const mode = obj.deliveryMode === '' ? null : obj.deliveryMode;
        const nature = mode === 1 ? 0 : mode === 2 ? 1 : null;
        setForm(f => ({ ...f, deliveryMode: mode, onlineNature: nature }));
    };

    const programOptions = programs
        .slice()
        .sort((a, b) => a.programId - b.programId)
        .map(p => ({
            value: p.programId,
            label: `${p.programId} - ${p.programFormalName}`,
            program: p,
            isDisabled: p.hasOpenRequest === true,
        }));

    const selectedProgramOptions = programOptions.filter(o =>
        form.sourceProgramIds.includes(o.value)
    );

    const handleProgramsChange = selected => {
        const sourceProgramIds = (selected ?? []).map(o => o.value);
        setForm(current => ({
            ...current,
            sourceProgramIds,
            sourceProgramTeachouts: sourceProgramIds.reduce((values, programId) => ({
                ...values,
                [programId]: current.sourceProgramTeachouts?.[programId] ?? {
                    teachoutFlag: null,
                    allStudentsTaughtOut: null,
                },
            }), {}),
        }));
    };

    const removeProgram = programId => {
        handleProgramsChange(selectedProgramOptions.filter(option => option.value !== programId));
    };

    const formatOptionLabel = ({ label, isDisabled }) =>
        isDisabled ? (
            <span className="text-muted">
                <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1 text-warning" />
                {label} — Pending request
            </span>
        ) : label;

    const handleSubmit = () => {
        setSubmitted(true);
        setSubmitError(null);
        if (
            hasValidationErrors(form) ||
            commentsLengthError(form.comments, maximumAdditionalCommentsLength)
        ) return;

        setSubmitting(true);

        const onError = apiErr => {
            const message =
                apiErr?.response?.data?.message ?? 'An unexpected error occurred. Please try again.';
            setSubmitError(message);
            setSubmitting(false);
        };

        submitFlow({ form, details: requestDetails, onError }).then(result => {
            setSubmitting(false);
            if (!result) return;
            onSuccess();
        });
    };

    const err = msg => <p className="text-danger mt-1">{msg}</p>;

    if (mode === 'edit' && initializingEdit) {
        return (
            <p className="text-muted small text-center py-4 mb-0">
                Loading request details…
            </p>
        );
    }

    return (
        <div>
            {mode !== 'edit' && (
                <button type="button" className="btn btn-link p-0 mb-3" onClick={onBack}>
                    ← Back
                </button>
            )}

            <InstitutionField
                institutionOptions={institutionOptions}
                value={form.institutionId}
                onChange={val => setField('institutionId', val)}
            />
            {submitted && !form.institutionId && err('Institution is required.')}

            {loadingPrograms && <p className="text-muted small mb-2">Loading programs…</p>}

            <FormGroup>
                <Label for="consolidate-programs">
                    Programs to Consolidate <sup className="text-danger">*</sup>
                </Label>
                <div className="text-muted small mb-1">Select 2 or more programs.</div>
                <Select
                    inputId="consolidate-programs"
                    isMulti
                    options={programOptions}
                    value={selectedProgramOptions}
                    onChange={handleProgramsChange}
                    placeholder={
                        form.sourceProgramIds.length > 0
                            ? 'Add another program…'
                            : form.institutionId
                                ? 'Search programs…'
                                : 'Select an institution first'
                    }
                    isDisabled={!form.institutionId || mode === 'edit'}
                    isClearable={false}
                    hideSelectedOptions
                    formatOptionLabel={formatOptionLabel}
                    isOptionDisabled={o => o.isDisabled}
                    controlShouldRenderValue={false}
                />
                {submitted && form.sourceProgramIds.length < 2 &&
                    err('At least 2 programs must be selected to consolidate.')}
            </FormGroup>

            {selectedProgramOptions.length > 0 && (
                <div className="mb-3">
                    <div className="small text-muted mb-1">
                        Selected ({selectedProgramOptions.length}):
                    </div>
                    <ul className="list-group">
                        {selectedProgramOptions.map(({ value, program }) => (
                            <li
                                key={value}
                                className="list-group-item py-2 d-flex justify-content-between align-items-center"
                            >
                                <div className="flex-grow-1 mr-3">
                                    <strong>{program.programFormalName}</strong>
                                    <span className="text-muted ml-2 small">
                                        ({program.programId})
                                        {program.programStatusDesc && ` · ${program.programStatusDesc}`}
                                        {program.cipSixDigitCode && ` · CIP ${program.cipSixDigitCode}`}
                                    </span>
                                    <div className="mt-3 border-top pt-3">
                                        <div className="row">
                                            <div className="col-md-6">
                                                <TeachoutField
                                                    id={`consolidate-teachout-${value}`}
                                                    value={form.sourceProgramTeachouts?.[value]?.teachoutFlag}
                                                    onChange={teachoutFlag => setProgramTeachout(value, {
                                                        teachoutFlag,
                                                        allStudentsTaughtOut: teachoutFlag === true
                                                            ? form.sourceProgramTeachouts?.[value]?.allStudentsTaughtOut ?? null
                                                            : null,
                                                    })}
                                                    submitted={submitted}
                                                />
                                            </div>
                                            {form.sourceProgramTeachouts?.[value]?.teachoutFlag === true && (
                                                <div className="col-md-6">
                                                    <AllStudentsTaughtOutField
                                                        id={`consolidate-all-taught-out-${value}`}
                                                        value={form.sourceProgramTeachouts?.[value]?.allStudentsTaughtOut}
                                                        onChange={allStudentsTaughtOut =>
                                                            setProgramTeachout(value, { allStudentsTaughtOut })}
                                                        submitted={submitted}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-link text-danger p-0 ml-2"
                                    aria-label={`Remove ${program.programFormalName}`}
                                    onClick={() => removeProgram(value)}
                                >
                                    ✕
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <hr />
            <h6 className="mb-3">New Consolidated Program</h6>

            <FormGroup>
                <Label for="consolidate-program-name">
                    Program Name <sup className="text-danger">*</sup>
                </Label>
                <Input
                    id="consolidate-program-name"
                    type="text"
                    placeholder="Program Name"
                    value={form.formalName}
                    onChange={e => setField('formalName', e.target.value)}
                />
                {submitted && !form.formalName?.trim() && err('Program Name is required.')}
            </FormGroup>

            <FormGroup>
                <Label for="consolidate-cip-code">
                    CIP Code <sup className="text-danger">*</sup>
                </Label>
                <Select
                    inputId="consolidate-cip-code"
                    options={cipOptions}
                    value={cipOptions.find(o => o.value === form.cipCode) ?? null}
                    onChange={opt => setField('cipCode', opt?.value ?? null)}
                    placeholder="Search CIP codes…"
                    isClearable
                    isLoading={loadingCips}
                />
                {submitted && !form.cipCode && err('CIP Code is required.')}
            </FormGroup>

            <FormGroup>
                <Label for="consolidate-locations">
                    Locations <sup className="text-danger">*</sup>
                </Label>
                {locationOptions.length === 1 ? (
                    <div className="form-control-plaintext">{locationOptions[0].label}</div>
                ) : (
                    <Select
                        inputId="consolidate-locations"
                        isMulti
                        options={locationOptions}
                        value={form.locations}
                        onChange={selected => setField('locations', selected ?? [])}
                        placeholder={
                            form.institutionId ? 'Select locations…' : 'Select an institution first'
                        }
                        isDisabled={!form.institutionId}
                        isClearable
                    />
                )}
                {submitted && !form.locations?.length && err('At least one Location is required.')}
            </FormGroup>

            <FormGroup>
                <Label for="consolidate-degree-level">
                    Degree / Award Level <sup className="text-danger">*</sup>
                </Label>
                <Select
                    inputId="consolidate-degree-level"
                    options={degreeLevelOptions}
                    value={degreeLevelOptions.find(o => o.value === form.degreeLevel) ?? null}
                    onChange={opt => setField('degreeLevel', opt?.value ?? null)}
                    placeholder="Select degree level…"
                    isClearable
                />
                {submitted && !form.degreeLevel && err('Degree/Award Level is required.')}
            </FormGroup>

            <FormGroup>
                <div className="row">
                    <div className="col-md-6">
                        <Label className="d-block">
                            Delivery Mode <sup className="text-danger">*</sup>
                        </Label>
                        <RadioCheckGroup
                            onChange={handleDeliveryModeChange}
                            name="deliveryMode"
                            value={form.deliveryMode}
                            options={DELIVERY_MODE_OPTIONS}
                        />
                        {submitted && form.deliveryMode === null && err('Delivery Mode is required.')}
                    </div>
                    {form.deliveryMode !== null && (
                        <div className="col-md-6 mt-3 mt-md-0">
                            <Label className="d-block">
                                Nature of Online Program
                                {form.deliveryMode === 3 && <sup className="text-danger"> *</sup>}
                            </Label>
                            {form.deliveryMode === 1 && (
                                <div className="form-control-plaintext">None</div>
                            )}
                            {form.deliveryMode === 2 && (
                                <div className="form-control-plaintext">100% Online</div>
                            )}
                            {form.deliveryMode === 3 && (
                                <>
                                    <RadioCheckGroup
                                        onChange={handleRadioChange('onlineNature')}
                                        name="onlineNature"
                                        value={form.onlineNature}
                                        options={ON_CAMPUS_ONLINE_NATURE_OPTIONS}
                                    />
                                    {submitted && form.onlineNature === null &&
                                        err('Nature of Online Program is required.')}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </FormGroup>

            <FormGroup>
                <Label>Credit Hours <sup className="text-danger">*</sup></Label>
                <div className="d-flex" style={{ gap: '1rem' }}>
                    <div className="flex-fill">
                        <Label for="consolidate-credit-min" className="small text-muted mb-1">
                            Min
                        </Label>
                        <Input
                            id="consolidate-credit-min"
                            type="number"
                            placeholder="Min"
                            value={form.creditMin}
                            onChange={e => setField('creditMin', e.target.value)}
                            min="0"
                        />
                    </div>
                    <div className="flex-fill">
                        <Label for="consolidate-credit-max" className="small text-muted mb-1">
                            Max
                        </Label>
                        <Input
                            id="consolidate-credit-max"
                            type="number"
                            placeholder="Max"
                            value={form.creditMax}
                            onChange={e => setField('creditMax', e.target.value)}
                            min="0"
                        />
                    </div>
                </div>
                {submitted && creditHoursError(form) && err(creditHoursError(form))}
            </FormGroup>

            <AdditionalCommentsField
                id="consolidate-comments"
                value={form.comments}
                onChange={value => setField('comments', value)}
                maximumLength={maximumAdditionalCommentsLength}
                submitted={submitted}
            />

            {submitError && (
                <div className="alert alert-danger mt-3" role="alert">
                    {submitError}
                </div>
            )}

            <div className="d-flex justify-content-end mt-4">
                <Button color="secondary" className="mr-2" onClick={onBack} disabled={submitting}>
                    Cancel
                </Button>
                <Button color="primary" onClick={handleSubmit} disabled={submitting}>
                    {submitting
                        ? mode === 'edit' ? 'Saving…' : 'Submitting…'
                        : mode === 'edit' ? 'Save Changes' : 'Submit Request'}
                </Button>
            </div>
        </div>
    );
};
